import type { ReactNode } from 'react';
import { Users } from 'lucide-react';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import { InfoHint } from '@/components/common/InfoHint';
import { UserChip } from '@/components/common/UserChip';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';
import { ENTITY_TYPES, ENTITY_LABEL, type EntityType } from '@/lib/constants/entity-types';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { TeamInvite, TeamMember } from '../api/team.api';
import { getMemberAccountAccess, type MemberAccountAccess } from '../member-account-access';
import { PermissionCell, InvitedToggle } from './PermissionCell';

export interface TeamMembersTableProps {
  /** Drives the role chip label ("{label} Admin"/"{label} Member") - defaults to startup for existing call sites. */
  entityType?: EntityType;
  /**
   * True for a Trusted Partner (see `TEAM_PAGE_FEATURES`). It no longer adds a
   * column: MYN-181 merged the separate "Create/Publish Offer Access" grant
   * into the edit toggle, so this only renames that one column to
   * "Edit Profile / Perk" and says so in its labels. Every other entity type
   * keeps the old name, because they have no perks to name.
   */
  hasPerks?: boolean;
  /**
   * Adds the Sector column for members and pending invites (MYN-202, FR-174).
   * Set from `TEAM_PAGE_FEATURES[entityType].inviteSector`.
   */
  showSector?: boolean;
  members: TeamMember[];
  invites: TeamInvite[];
  onTogglePermission: (
    membershipId: string,
    permissionKey: typeof PERMISSIONS.ENTITY_PROFILE_EDIT | typeof PERMISSIONS.TEAM_INVITE,
    isEnabled: boolean,
  ) => void;
  onChangeRole: (membershipId: string, role: 'admin' | 'member') => void;
  onRemove: (membershipId: string) => void;
  /** Removes a pending invite (revokes it). If they still need access, re-invite. */
  onRevokeInvite: (inviteId: string) => void;
  /**
   * True for a viewer who holds `team.invite` but not `team.manage` (the
   * backend admits them to `GET .../team` for exactly this reason - see
   * `TeamController.listMembers`). They can see the full roster but must not
   * be able to change it: permission toggles render as static state, the
   * Assign Admin/Make Member link is hidden, and Remove Member is hidden.
   */
  readOnly?: boolean;
  /** Data Room Access cell per member; omit to hide the column (see `useDataRoomSeatColumn`). */
  renderDataRoomSeat?: (member: TeamMember) => ReactNode;
}

/**
 * Merged row: an active/pending member (with its account-status access
 * precomputed once via `getMemberAccountAccess`, see that module for why),
 * or a pending invite (no account yet, so no `accountStatus` to read).
 */
type Row =
  | { kind: 'member'; member: TeamMember; accountAccess: MemberAccountAccess }
  | { kind: 'invite'; invite: TeamInvite };

/**
 * Functional role label shown in the ROLE column, genericized off
 * `ENTITY_LABEL` (the owner reads as "{label} Admin" here - their ownership is
 * surfaced in the ACTIONS column instead - per the Figma teams-and-permissions
 * design).
 */
function roleLabel(entityType: EntityType, role: TeamMember['role'] | TeamInvite['role']): string {
  const label = ENTITY_LABEL[entityType];
  return role === 'member' ? `${label} Member` : `${label} Admin`;
}

/**
 * Extra left inset on the toggle columns (MYN-181 AC5). `DataTable` gives every
 * column a uniform `pr-4`, which is tight enough here that a switch reads as
 * belonging to the column on its left. Applied per column on this screen rather
 * than widened in the shared primitive, which every other admin listing uses.
 */
const TOGGLE_COLUMN_CLASS = 'pl-8';

/**
 * Copy for the single edit-access column, computed once from whether this
 * entity has perks rather than re-tested at the header and at each of the two
 * label sites (Style Rules - no repeated branching on one discriminant).
 * A partner's grant covers offers as well, and the column has to say so.
 * `info` (MYN-280, FR-195) is the column header's info-pop-up copy.
 */
const EDIT_ACCESS_COPY = {
  withPerks: {
    header: 'Edit Profile / Perk',
    label: 'Edit profile and perk access',
    info: 'Allows this member to edit your profile and manage your perks.',
  },
  profileOnly: {
    header: 'Edit Profile Access',
    label: 'Edit profile access',
    info: 'Allows this member to edit your profile information.',
  },
} as const;

/** Invite Members Access column header's info-pop-up copy (MYN-280, FR-195). */
const INVITE_ACCESS_INFO = 'Allows this member to invite new people to join your team.';

/** Two-letter avatar initials from an email local part (invited users have no name). */
function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? email;
  const letters = local.replace(/[^a-zA-Z]/g, '');
  return (letters.slice(0, 2) || local.slice(0, 2)).toUpperCase();
}

/**
 * The Team Members table (MYN-43). Shows active/pending members and pending
 * invites (people invited but not yet joined). Owner/admin roles show "Full
 * Access" for both permissions; plain members get interactive toggles; pending
 * invites show disabled toggles (permissions can't be granted until they join).
 * A Data Room Access column joins them while a two-seat service is current
 * (FR-139, `renderDataRoomSeat`); pending invites cannot hold a seat.
 *
 * `readOnly` (set by `TeamPermissionsPage` for a viewer who holds only
 * `team.invite`, not `team.manage`) renders every member's toggle as
 * disabled/static and hides Assign Admin/Make Member and Remove Member -
 * visible roster, zero mutating controls.
 *
 * A member whose `accountStatus` (see `team.api.ts` - distinct from the
 * membership `status` above) is not 'active' gets a status badge, disabled
 * permission toggles, and no Assign Admin/Make Member link (MYN-133/135: a
 * Super Admin can suspend an account from the users console without this
 * page knowing, unless it reads this field). Remove Member stays available -
 * removing a suspended person from the team is still a legitimate action.
 */
export function TeamMembersTable({
  entityType = ENTITY_TYPES.STARTUP,
  hasPerks = false,
  showSector = false,
  members,
  invites,
  onTogglePermission,
  onChangeRole,
  onRemove,
  onRevokeInvite,
  readOnly = false,
  renderDataRoomSeat,
}: TeamMembersTableProps) {
  const rows: Row[] = [
    ...members.map((member): Row => ({
      kind: 'member',
      member,
      accountAccess: getMemberAccountAccess(member),
    })),
    ...invites.map((invite): Row => ({ kind: 'invite', invite })),
  ];

  const editAccess = hasPerks ? EDIT_ACCESS_COPY.withPerks : EDIT_ACCESS_COPY.profileOnly;

  const columns: DataTableColumn<Row>[] = [
    {
      id: 'member',
      header: 'Member',
      cell: (row) =>
        row.kind === 'member' ? (
          // Badges go through UserChip's `nameSuffix` so they sit beside the
          // NAME. As siblings of the whole chip they drifted right of the
          // avatar/name/email block and read as belonging to the next column.
          <UserChip
            name={row.member.name}
            caption={row.member.email}
            nameSuffix={
              <>
                {row.member.status === 'pending' && (
                  <Badge variant="pending" size="sm">Pending</Badge>
                )}
                {/* Account-status badge: the Super Admin can suspend a member's
                    account from the users console without touching their team
                    membership, so this is the only place on this page that
                    would otherwise say so. */}
                {row.accountAccess.badge && (
                  <Badge variant={row.accountAccess.badge.variant} size="sm">
                    {row.accountAccess.badge.label}
                  </Badge>
                )}
              </>
            }
          />
        ) : (
          // Invited user: no account yet — avatar/name from the invitee name
          // when the inviter provided one, falling back to email-initials-only
          // (an invite created before this change, or one left blank) — plus
          // the Pending chip and the email small + truncated so a long address
          // can't blow out the column width.
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green font-sans text-sm font-semibold text-white"
            >
              {row.invite.inviteeName
                ? initialsFromName(row.invite.inviteeName)
                : emailInitials(row.invite.email)}
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 items-center gap-2">
                <Badge variant="pending" size="sm" className="w-fit shrink-0">Pending</Badge>
                {row.invite.inviteeName && (
                  <span
                    className="min-w-0 truncate text-sm font-semibold text-navy"
                    title={row.invite.inviteeName}
                  >
                    {row.invite.inviteeName}
                  </span>
                )}
              </div>
              <span className="block max-w-55 truncate text-xs text-slate" title={row.invite.email}>
                {row.invite.email}
              </span>
            </div>
          </div>
        ),
    },
    ...(showSector
      ? [
          {
            id: 'sector',
            header: TEAM_STRINGS.INVITE_SECTOR.COLUMN_HEADER,
            cell: (row: Row) => {
              const sector = row.kind === 'member' ? row.member.sector : row.invite.sector;
              return (
                <span className="text-sm text-navy">
                  {sector?.name ?? TEAM_STRINGS.INVITE_SECTOR.NONE}
                </span>
              );
            },
          } satisfies DataTableColumn<Row>,
        ]
      : []),
    {
      id: 'edit',
      // The info icon is wrapped on its own, not alongside the header text -
      // `DataTable` renders `column.header` directly inside the `<th>` with
      // no other wrapping element, so a shared container around both would
      // duplicate the header text's own textContent on an ancestor node and
      // break every existing `getByText(editAccess.header)` test assertion.
      header: (
        <>
          {editAccess.header}
          <span className="ml-1.5 inline-flex align-middle">
            <InfoHint content={editAccess.info} label={`What does "${editAccess.header}" allow?`} />
          </span>
        </>
      ),
      className: TOGGLE_COLUMN_CLASS,
      cell: (row) =>
        row.kind === 'member' ? (
          <PermissionCell
            member={row.member}
            enabled={row.member.permissions.entityProfileEdit}
            onToggle={(next) =>
              onTogglePermission(
                row.member.membershipId,
                PERMISSIONS.ENTITY_PROFILE_EDIT,
                next,
              )
            }
            label={`${editAccess.label} for ${row.member.name}`}
            readOnly={readOnly}
            accountAccess={row.accountAccess}
          />
        ) : (
          <InvitedToggle label={`${editAccess.label} (available after the invite is accepted)`} />
        ),
    },
    {
      id: 'invite',
      header: (
        <>
          Invite Members Access
          <span className="ml-1.5 inline-flex align-middle">
            <InfoHint content={INVITE_ACCESS_INFO} label='What does "Invite Members Access" allow?' />
          </span>
        </>
      ),
      className: TOGGLE_COLUMN_CLASS,
      cell: (row) =>
        row.kind === 'member' ? (
          <PermissionCell
            member={row.member}
            enabled={row.member.permissions.teamInvite}
            onToggle={(next) =>
              onTogglePermission(row.member.membershipId, PERMISSIONS.TEAM_INVITE, next)
            }
            label={`Invite members access for ${row.member.name}`}
            readOnly={readOnly}
            accountAccess={row.accountAccess}
          />
        ) : (
          <InvitedToggle label="Invite members access (available after the invite is accepted)" />
        ),
    },
    ...(renderDataRoomSeat
      ? [
          {
            id: 'dataRoom',
            header: TEAM_STRINGS.DATA_ROOM_SEATS.COLUMN,
            className: TOGGLE_COLUMN_CLASS,
            cell: (row: Row) => (row.kind === 'member' ? renderDataRoomSeat(row.member) : null),
          },
        ]
      : []),
    // A separate "Create/Publish Offer Access" column stood here until
    // MYN-181. Granting a partner member "can edit our stuff" took two
    // toggles, and holding one without the other was a state nobody wanted,
    // so the permission itself was merged into Edit Profile / Perk above.
    {
      id: 'role',
      header: 'Role',
      cell: (row) => {
        if (row.kind === 'invite') {
          return (
            <Badge variant="chip" size="sm" className="whitespace-nowrap font-semibold">
              {roleLabel(entityType, row.invite.role)}
            </Badge>
          );
        }
        const { member } = row;
        // The current user's row gets the dark navy role chip ("{label} Admin · You").
        if (member.isSelf) {
          return (
            <Badge variant="dark" size="sm" className="whitespace-nowrap">
              {`${roleLabel(entityType, member.role)} · You`}
            </Badge>
          );
        }
        // Non-self members: role label as a pale chip (Figma), with the
        // Assign Admin / Make Member link directly beneath it.
        return (
          <div className="inline-flex flex-col items-center gap-1">
            <Badge variant="chip" size="sm" className="whitespace-nowrap font-semibold">
              {roleLabel(entityType, member.role)}
            </Badge>
            {/* Not offered for a suspended/deactivated/deleted account (MYN-133/135)
                - promoting or demoting someone who cannot sign in is a
                no-op that would only confuse the operator. */}
            {!member.isOwner && !readOnly && row.accountAccess.isActive && (
              <Button
                type="button"
                variant="link"
                className="self-center text-xs"
                onClick={() =>
                  onChangeRole(
                    member.membershipId,
                    member.role === 'member' ? 'admin' : 'member',
                  )
                }
              >
                {member.role === 'member' ? 'Assign Admin' : 'Make Member'}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      className: 'text-right whitespace-nowrap',
      cell: (row) => {
        if (row.kind === 'invite') {
          // Single "Cancel invite" action (no separate Resend) — to resend, an
          // admin cancels the invite and sends a new one. A read-only (invite-only)
          // viewer sees the row but has no action available for it.
          if (readOnly) return null;
          return (
            <div className="text-right">
              <Button
                type="button"
                variant="link"
                className="text-xs text-pink hover:text-pink-hover"
                onClick={() => onRevokeInvite(row.invite.id)}
              >
                Cancel invite
              </Button>
            </div>
          );
        }
        const { member } = row;
        if (member.isOwner) {
          return <span className="block text-right text-sm text-slate">Owner</span>;
        }
        if (member.isSelf) {
          return <span className="block text-right text-sm text-slate">You</span>;
        }
        if (readOnly) return null;
        return (
          <div className="text-right">
            <Button
              type="button"
              variant="link"
              className="text-xs text-pink hover:text-pink-hover"
              onClick={() => onRemove(member.membershipId)}
            >
              Remove Member
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      caption="Team members and pending invites"
      columns={columns}
      rows={rows}
      rowKey={(row) => (row.kind === 'member' ? `m-${row.member.membershipId}` : `i-${row.invite.id}`)}
      emptyState={{
        icon: <Users size={32} aria-hidden="true" />,
        title: 'No team members yet',
        description: `Invite a teammate to collaborate on your ${ENTITY_LABEL[entityType].toLowerCase()} profile.`,
      }}
    />
  );
}

